import React, { useEffect, useState } from 'react'
import DropdownInput, { DropdownOption } from './DropdownInput'
import labelService, { LabelNode } from '../services/labelService'

interface CreateLabelDataModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: LabelFormData) => Promise<void>
  /** 관리자/label_project_manage 전역 권한 사용자는 프로젝트 정의를 직접 입력할 수 있음 */
  canDirectProjectInput?: boolean
}

export interface LabelFormData {
  name: string
  group: string
  version: string
  projectDescription?: string
  groupDescription?: string
  versionDescription?: string
  existingProjectId?: number
}

export default function CreateLabelDataModal({ isOpen, onClose, onSubmit, canDirectProjectInput }: CreateLabelDataModalProps) {
  const MAX_DESC = 1000
  const [formData, setFormData] = useState<LabelFormData>({ name: '', group: '', version: '', projectDescription: '', groupDescription: '', versionDescription: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [projects, setProjects] = useState<LabelNode[]>([])
  const [groups, setGroups] = useState<LabelNode[]>([])
  const [versions, setVersions] = useState<LabelNode[]>([])
  const [selProject, setSelProject] = useState<LabelNode | null>(null)
  const [selGroup, setSelGroup] = useState<LabelNode | null>(null)
  const [selVersion, setSelVersion] = useState<LabelNode | null>(null)

  useEffect(() => {
    if (!isOpen) return
    ;(async () => {
      try { setProjects(await labelService.getRootNodes()) } catch { setProjects([]) }
      setGroups([]); setVersions([])
      setSelProject(null); setSelGroup(null); setSelVersion(null)
      setFormData({ name: '', group: '', version: '', projectDescription: '', groupDescription: '', versionDescription: '' })
    })()
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // 1 또는 1-2 또는 1-2-3만 허용
    if (!formData.name) {
      alert('프로젝트명(1단계)은 필수입니다.')
      return
    }
    if (formData.version && !formData.group) {
      alert('3단계를 입력하려면 2단계를 먼저 입력해야 합니다.')
      return
    }
    try {
      setIsSubmitting(true)
      await onSubmit({
        ...formData,
        existingProjectId: selProject ? (selProject.project_id || selProject.id) : undefined
      })
      alert('✅ 라벨 데이터 구조가 생성되었습니다!')
      onClose()
      setFormData({ name: '', group: '', version: '', projectDescription: '', groupDescription: '', versionDescription: '' })
    } catch (err: any) {
      let msg = '라벨 데이터 생성에 실패했습니다.'
      if (err?.message?.includes('already exists')) {
        const s = err.message.match(/'([^']+)'/)?.[1]
        msg = s ? `⚠️ 중복된 구조입니다: ${s}` : '⚠️ 이미 존재하는 구조입니다.'
      } else if (typeof err?.message === 'string') {
        msg = `❌ ${err.message}`
      }
      alert(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
      setFormData({ name: '', group: '', version: '', projectDescription: '', groupDescription: '', versionDescription: '' })
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">라벨 데이터 생성</h2>
          <button onClick={handleClose} disabled={isSubmitting} className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <DropdownInput
              label={"프로젝트 정의 *"}
              value={formData.name}
              onChange={(v) => setFormData(fd => ({ ...fd, name: v }))}
              options={projects as unknown as DropdownOption[]}
              placeholder={projects.length === 0 ? '생성 권한이 있는 라벨 프로젝트가 없습니다' : '프로젝트를 선택하거나 직접 입력하세요'}
              // 관리자/label_project_manage 권한자는 직접입력 허용, 그 외에는 선택만 가능
              inputDisabled={isSubmitting || !canDirectProjectInput || !!selProject}
              dropdownDisabled={isSubmitting || projects.length === 0}
              onPickExisting={async (opt) => {
                const node = projects.find(p => String(p.id) === String(opt.id)) || null
                setSelProject(node)
                setFormData(fd => ({ ...fd, name: opt.name, projectDescription: (node?.description as any) || '', group: '', version: '', groupDescription: '', versionDescription: '' }))
                setSelGroup(null); setSelVersion(null)
                try { setGroups(await labelService.getNodeChildren(node!.id)) } catch { setGroups([]) }
                setVersions([])
              }}
              onDirectInput={canDirectProjectInput ? () => {
                setSelProject(null)
                setGroups([]); setVersions([])
                setSelGroup(null); setSelVersion(null)
                setFormData(fd => ({ ...fd, name: '', projectDescription: '', group: '', version: '', groupDescription: '', versionDescription: '' }))
              } : undefined}
            />
            {/* 프로젝트 설명: 프로젝트 정의 바로 아래 */}
            <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">프로젝트 설명</label>
            <textarea
              value={selProject ? ((selProject?.description as any) || '') : (formData.projectDescription || '')}
              onChange={(e) => { if (!selProject) setFormData({ ...formData, projectDescription: e.target.value }) }}
              className={`w-full px-3 py-2 border rounded-md ${!selProject ? 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent' : 'border-gray-200 bg-gray-50 text-gray-600 opacity-60 cursor-not-allowed focus:outline-none focus:ring-0'}`}
              rows={2}
              placeholder="프로젝트 설명 (선택)"
              maxLength={MAX_DESC}
              readOnly={!!selProject}
              aria-disabled={!!selProject}
              title={selProject ? '기존 항목의 설명입니다 (수정 불가)' : undefined}
            />
            {!selProject && (
              <div className="mt-1 text-right text-xs text-gray-500">{(formData.projectDescription || '').length}/{MAX_DESC}</div>
            )}
          </div>
          <div>
            <DropdownInput
              label={"데이터 정의"}
              value={formData.group}
              onChange={(v) => setFormData(fd => ({ ...fd, group: v }))}
              options={groups as unknown as DropdownOption[]}
              placeholder={"예: 자율주행"}
              inputDisabled={isSubmitting || !!selGroup}
              dropdownDisabled={isSubmitting || !(selProject || (formData.name||'').trim())}
              onPickExisting={async (opt) => {
                const node = groups.find(g => String(g.id) === String(opt.id)) || null
                setSelGroup(node)
                setFormData(fd => ({ ...fd, group: opt.name, groupDescription: (node?.description as any) || '', version: '', versionDescription: '' }))
                setSelVersion(null)
                try { setVersions(await labelService.getNodeChildren(node!.id)) } catch { setVersions([]) }
              }}
              onDirectInput={() => {
                setSelGroup(null)
                setVersions([]); setSelVersion(null)
                setFormData(fd => ({ ...fd, group: '', groupDescription: '', version: '', versionDescription: '' }))
              }}
            />
            {/* 데이터 설명: 데이터 정의 바로 아래 */}
            <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">데이터 설명</label>
            <textarea
              value={selGroup ? ((selGroup?.description as any) || '') : (formData.groupDescription || '')}
              onChange={(e) => { if (!selGroup) setFormData({ ...formData, groupDescription: e.target.value }) }}
              className={`w-full px-3 py-2 border rounded-md ${!selGroup ? 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent' : 'border-gray-200 bg-gray-50 text-gray-600 opacity-60 cursor-not-allowed focus:outline-none focus:ring-0'}`}
              rows={2}
              placeholder="데이터 설명 (선택)"
              maxLength={MAX_DESC}
              readOnly={!!selGroup}
              aria-disabled={!!selGroup}
              title={selGroup ? '기존 항목의 설명입니다 (수정 불가)' : undefined}
            />
            {!selGroup && (
              <div className="mt-1 text-right text-xs text-gray-500">{(formData.groupDescription || '').length}/{MAX_DESC}</div>
            )}
          </div>
          <div>
            <DropdownInput
              label={"버전"}
              value={formData.version}
              onChange={(v) => setFormData(fd => ({ ...fd, version: v }))}
              options={versions as unknown as DropdownOption[]}
              placeholder={"예: v1_202506"}
              inputDisabled={isSubmitting || !!selVersion}
              dropdownDisabled={isSubmitting || !(selGroup || (formData.group||'').trim())}
              onPickExisting={async (opt) => {
                const node = versions.find(s => String(s.id) === String(opt.id)) || null
                setSelVersion(node)
                setFormData(fd => ({ ...fd, version: opt.name, versionDescription: (node?.description as any) || '' }))
              }}
              onDirectInput={() => {
                setSelVersion(null)
                setFormData(fd => ({ ...fd, version: '', versionDescription: '' }))
              }}
            />
            {/* 버전 설명: 버전 입력 바로 아래 */}
            <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">버전 설명</label>
            <textarea
              value={selVersion ? ((selVersion?.description as any) || '') : (formData.versionDescription || '')}
              onChange={(e) => { if (!selVersion) setFormData({ ...formData, versionDescription: e.target.value }) }}
              className={`w-full px-3 py-2 border rounded-md ${!selVersion ? 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent' : 'border-gray-200 bg-gray-50 text-gray-600 opacity-60 cursor-not-allowed focus:outline-none focus:ring-0'}`}
              rows={2}
              placeholder="버전 설명 (선택)"
              maxLength={MAX_DESC}
              readOnly={!!selVersion}
              aria-disabled={!!selVersion}
              title={selVersion ? '기존 항목의 설명입니다 (수정 불가)' : undefined}
            />
            {!selVersion && (
              <div className="mt-1 text-right text-xs text-gray-500">{(formData.versionDescription || '').length}/{MAX_DESC}</div>
            )}
          </div>
          <div className="flex space-x-3 pt-4">
            <button type="button" onClick={handleClose} disabled={isSubmitting} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50">취소</button>
            <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900 disabled:opacity-50">{isSubmitting ? '생성 중...' : '생성'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}



